import { notificationHistory, currentUser } from './database'
import { getIntegrationSettings, postSlackMessage } from '../lib/integrations'
import { reminderDueAt } from '../lib/reminderTiming'
import { sourceId } from '../lib/sourceMapping'

// Note: notificationHistory import is used in sendReminder via logging

// Notification Service - handles sending notifications via multiple channels
export class NotificationService {
  async sendReminder(reminder, roadmapItem, notificationChannels) {
    try {
      const results = {
        success: [],
        failed: []
      }

      for (const channel of notificationChannels) {
        try {
          let result = null
          switch (channel) {
            case 'slack':
              result = await this.sendSlackReminder(reminder, roadmapItem)
              break
            case 'in_app':
              result = await this.sendInAppReminder(reminder, roadmapItem)
              break
            case 'calendar':
              result = await this.sendCalendarReminder(reminder, roadmapItem)
              break
            case 'email':
              result = await this.sendEmailReminder(reminder, roadmapItem)
              break
            default:
              console.warn(`Unknown notification channel: ${channel}`)
              results.failed.push(channel)
              continue
          }

          if (result) {
            // Log successful notification
            if (channel !== 'in_app') await notificationHistory.logNotification(
              reminder.id,
              channel,
              'sent',
              result.channelDetails || {}
            )
            results.success.push(channel)
          } else {
            results.failed.push(channel)
          }
        } catch (error) {
          console.error(`Error sending ${channel} reminder:`, error)
          results.failed.push(channel)
          // Log failed notification
          await notificationHistory.logNotification(
            reminder.id,
            channel,
            'failed',
            {},
            error.message
          )
        }
      }

      return results
    } catch (error) {
      console.error('Error in sendReminder:', error)
      throw error
    }
  }

  async sendSlackReminder(reminder, roadmapItem) {
    try {
      const { slack } = await getIntegrationSettings()
      if (!slack.channel) {
        console.warn('Slack channel not set for user')
        return null
      }

      // Posts with the user's own Slack connection (integrations API)
      const data = await postSlackMessage({ channel: slack.channel, ...this.formatSlackMessage(reminder, roadmapItem) })
      return {
        channelDetails: {
          channel: slack.channel,
          ts: data.ts
        }
      }
    } catch (error) {
      console.error('Error sending Slack reminder:', error)
      throw error
    }
  }

  async sendInAppReminder(reminder, roadmapItem) {
    const owner = await currentUser()
    const id = await sourceId(owner.id, 'reminder-notification', `${reminder.id}:${reminderDueAt(reminder, Intl.DateTimeFormat().resolvedOptions().timeZone)}`)
    if (!await notificationHistory.getById(id)) {
      const title = reminder.title || roadmapItem?.title || 'SPCC reminder'
      await notificationHistory.create({ id, reminder_id: reminder.id, notification_type: 'in_app', title, status: 'sent', sent_at: new Date().toISOString(), read_at: null, channel_details: {} })
      if ((await currentUser()).id === owner.id) window.dispatchEvent(new CustomEvent('spcc:reminder', { detail: { id, title } }))
    }
    return {
      channelDetails: {
        type: 'in_app'
      }
    }
  }

  async sendCalendarReminder() { throw new Error('Calendar delivery is not configured.') }
  async sendEmailReminder() { throw new Error('Email delivery is not configured.') }

  formatSlackMessage(reminder, roadmapItem) {
    const daysLeft = this.calculateDaysUntilDue(reminder.target_date)
    const reminderEmoji = daysLeft <= 0 ? '🚨' : daysLeft <= 3 ? '⚠️' : '⏰'

    return {
      text: `${reminderEmoji} Discovery Reminder: ${roadmapItem.title}`,
      blocks: [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `${reminderEmoji} *Discovery Due for Initiative*\n*${roadmapItem.title}*`
          }
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*Due Date:*\n${this.formatDate(reminder.target_date)}`
            },
            {
              type: 'mrkdwn',
              text: `*Days Until Due:*\n${daysLeft > 0 ? daysLeft + ' days' : 'Overdue'}`
            }
          ]
        },
        ...(reminder.description ? [
          {
            type: 'section',
            text: {
              type: 'mrkdwn',
              text: `*Details:*\n${reminder.description}`
            }
          }
        ] : []),
        {
          type: 'actions',
          elements: [
            {
              type: 'button',
              text: {
                type: 'plain_text',
                text: 'View in SPCC'
              },
              url: `${window.location.origin}/roadmap#${roadmapItem.id}`
            }
          ]
        }
      ]
    }
  }

  formatEmailReminder(reminder, roadmapItem, email) {
    const daysLeft = this.calculateDaysUntilDue(reminder.target_date)
    return {
      to: email,
      subject: `Reminder: Discovery due for "${roadmapItem.title}"`,
      html: `
        <h2>Discovery Reminder</h2>
        <p>Hello,</p>
        <p>This is a reminder that discovery is due for the initiative: <strong>${roadmapItem.title}</strong></p>
        <ul>
          <li><strong>Due Date:</strong> ${this.formatDate(reminder.target_date)}</li>
          <li><strong>Days Until Due:</strong> ${daysLeft > 0 ? daysLeft + ' days' : 'Overdue'}</li>
          ${reminder.description ? `<li><strong>Details:</strong> ${reminder.description}</li>` : ''}
        </ul>
        <p>
          <a href="${window.location.origin}/roadmap#${roadmapItem.id}">View in SPCC</a>
        </p>
      `
    }
  }

  calculateDaysUntilDue(targetDate) {
    const today = new Date().setHours(0, 0, 0, 0)
    const due = new Date(targetDate).setHours(0, 0, 0, 0)
    return Math.floor((due - today) / (1000 * 60 * 60 * 24))
  }

  formatDate(dateStr) {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }
    return new Date(dateStr).toLocaleDateString(undefined, options)
  }
}

export const notificationService = new NotificationService()
